import { makeAutoObservable } from 'mobx';
import { type MusicFile } from './libraryStore';

export interface Track {
    id: string;
    title: string;
    artist: string;
    album: string;
    year?: string;
    duration?: number;
    trackNumber?: number;
    musicFile: MusicFile;
}

export interface Playlist {
    id: string;
    name: string;
    tracks: Track[];
    createdAt: Date;
    updatedAt: Date;
}

class PlaylistStore {
    currentPlaylist: Playlist | null = null;
    currentTrackIndex: number = -1;
    playlists: Playlist[] = [];
    isShuffleEnabled: boolean = false;
    isRepeatEnabled: boolean = false;
    repeatMode: 'none' | 'track' | 'playlist' = 'none';

    // Shuffle state
    private shuffledIndices: number[] = [];
    private currentShuffleIndex: number = -1;

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * Create a new playlist from an array of music files
     */
    createPlaylist(name: string, musicFiles: MusicFile[]): Playlist {
        const playlist: Playlist = {
            id: this.generateId(),
            name,
            tracks: musicFiles.map(file => this.createTrackFromMusicFile(file)),
            createdAt: new Date(),
            updatedAt: new Date()
        };

        this.playlists.push(playlist);
        return playlist;
    }

    /**
     * Create a playlist from album tracks and set it as current
     */
    createPlaylistFromAlbum(musicFiles: MusicFile[]): Playlist {
        if (musicFiles.length === 0) {
            throw new Error('No music files provided to create playlist from album.');
        }

        const playlistName = `${musicFiles[0].album ?? 'Unknown Album'} - ${musicFiles[0].artist ?? 'Unknown Artist'}`;

        // Sort tracks by track number or filename if track number is not available
        const sortedFiles = [...musicFiles].sort((a, b) => {
            const aTrack = a.trackNumber;
            const bTrack = b.trackNumber;

            if (aTrack !== undefined && bTrack !== undefined && aTrack !== bTrack) {
                return aTrack - bTrack;
            }

            if (aTrack !== undefined && bTrack === undefined) {
                return -1;
            }

            if (aTrack === undefined && bTrack !== undefined) {
                return 1;
            }

            const aName = a.title || a.name;
            const bName = b.title || b.name;
            return aName.localeCompare(bName);
        });

        const playlist = this.createPlaylist(playlistName, sortedFiles);
        this.setCurrentPlaylist(playlist);
        return playlist;
    }

    /**
     * Set the current playlist and reset playback position
     */
    setCurrentPlaylist(playlist: Playlist): void {
        this.currentPlaylist = playlist;
        this.currentTrackIndex = 0;
        this.resetShuffle();
    }

    /**
     * Get the current track
     */
    getCurrentTrack(): Track | null {
        if (!this.currentPlaylist || this.currentTrackIndex < 0) {
            return null;
        }

        const index = this.isShuffleEnabled ?
            this.shuffledIndices[this.currentShuffleIndex] :
            this.currentTrackIndex;

        return this.currentPlaylist.tracks[index] || null;
    }

    /**
     * Move to the next track in the playlist
     */
    nextTrack(): Track | null {
        if (!this.currentPlaylist) {
            return null;
        }

        if (this.isShuffleEnabled) {
            return this.nextShuffledTrack();
        }

        if (this.currentTrackIndex < this.currentPlaylist.tracks.length - 1) {
            this.currentTrackIndex++;
            return this.getCurrentTrack();
        } else if (this.repeatMode === 'playlist') {
            this.currentTrackIndex = 0;
            return this.getCurrentTrack();
        }

        return null;
    }

    /**
     * Move to the previous track in the playlist
     */
    previousTrack(): Track | null {
        if (!this.currentPlaylist) {
            return null;
        }

        if (this.isShuffleEnabled) {
            return this.previousShuffledTrack();
        }

        if (this.currentTrackIndex > 0) {
            this.currentTrackIndex--;
            return this.getCurrentTrack();
        } else if (this.repeatMode === 'playlist') {
            this.currentTrackIndex = this.currentPlaylist.tracks.length - 1;
            return this.getCurrentTrack();
        }

        return null;
    }

    /**
     * Check if there is a next track available
     */
    hasNextTrack(): boolean {
        if (!this.currentPlaylist) {
            return false;
        }

        if (this.isShuffleEnabled) {
            return this.currentShuffleIndex < this.shuffledIndices.length - 1 || this.repeatMode === 'playlist';
        }

        return this.currentTrackIndex < this.currentPlaylist.tracks.length - 1 || this.repeatMode === 'playlist';
    }

    /**
     * Check if there is a previous track available
     */
    hasPreviousTrack(): boolean {
        if (!this.currentPlaylist) {
            return false;
        }

        if (this.isShuffleEnabled) {
            return this.currentShuffleIndex > 0 || this.repeatMode === 'playlist';
        }

        return this.currentTrackIndex > 0 || this.repeatMode === 'playlist';
    }

    /**
     * Toggle shuffle mode
     */
    toggleShuffle(): void {
        this.isShuffleEnabled = !this.isShuffleEnabled;

        if (this.isShuffleEnabled) {
            this.initializeShuffle();
        } else {
            this.resetShuffle();
        }
    }

    /**
     * Set repeat mode
     */
    setRepeatMode(mode: 'none' | 'track' | 'playlist'): void {
        this.repeatMode = mode;
        this.isRepeatEnabled = mode !== 'none';
    }

    /**
     * Toggle repeat mode (cycles through none -> track -> playlist -> none)
     */
    toggleRepeat(): void {
        switch (this.repeatMode) {
            case 'none':
                this.setRepeatMode('track');
                break;
            case 'track':
                this.setRepeatMode('playlist');
                break;
            case 'playlist':
                this.setRepeatMode('none');
                break;
        }
    }

    /**
     * Clear the current playlist
     */
    clearCurrentPlaylist(): void {
        this.currentPlaylist = null;
        this.currentTrackIndex = -1;
        this.resetShuffle();
    }

    /**
     * Delete a playlist by ID
     */
    deletePlaylist(playlistId: string): void {
        const index = this.playlists.findIndex(p => p.id === playlistId);
        if (index !== -1) {
            this.playlists.splice(index, 1);

            // Clear current playlist if it was deleted
            if (this.currentPlaylist?.id === playlistId) {
                this.clearCurrentPlaylist();
            }
        }
    }

    // Private helper methods

    private createTrackFromMusicFile(musicFile: MusicFile): Track {
        return {
            id: this.generateId(),
            title: musicFile.title || musicFile.name,
            artist: musicFile.artist || 'Unknown Artist',
            album: musicFile.album || 'Unknown Album',
            year: musicFile.year,
            duration: musicFile.duration,
            trackNumber: musicFile.trackNumber,
            musicFile
        };
    }

    private generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    private initializeShuffle(): void {
        if (!this.currentPlaylist) {
            return;
        }

        // Create array of indices and shuffle them
        this.shuffledIndices = Array.from({ length: this.currentPlaylist.tracks.length }, (_, i) => i);

        // Fisher-Yates shuffle
        for (let i = this.shuffledIndices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.shuffledIndices[i], this.shuffledIndices[j]] = [this.shuffledIndices[j], this.shuffledIndices[i]];
        }

        // Find current track in shuffled array
        this.currentShuffleIndex = this.shuffledIndices.indexOf(this.currentTrackIndex);
    }

    private resetShuffle(): void {
        this.shuffledIndices = [];
        this.currentShuffleIndex = -1;
    }

    private nextShuffledTrack(): Track | null {
        if (!this.currentPlaylist || this.shuffledIndices.length === 0) {
            return null;
        }

        if (this.currentShuffleIndex < this.shuffledIndices.length - 1) {
            this.currentShuffleIndex++;
            this.currentTrackIndex = this.shuffledIndices[this.currentShuffleIndex];
            return this.getCurrentTrack();
        } else if (this.repeatMode === 'playlist') {
            this.currentShuffleIndex = 0;
            this.currentTrackIndex = this.shuffledIndices[this.currentShuffleIndex];
            return this.getCurrentTrack();
        }

        return null;
    }

    private previousShuffledTrack(): Track | null {
        if (!this.currentPlaylist || this.shuffledIndices.length === 0) {
            return null;
        }

        if (this.currentShuffleIndex > 0) {
            this.currentShuffleIndex--;
            this.currentTrackIndex = this.shuffledIndices[this.currentShuffleIndex];
            return this.getCurrentTrack();
        } else if (this.repeatMode === 'playlist') {
            this.currentShuffleIndex = this.shuffledIndices.length - 1;
            this.currentTrackIndex = this.shuffledIndices[this.currentShuffleIndex];
            return this.getCurrentTrack();
        }

        return null;
    }
}

export const playlistStore = new PlaylistStore();